import React, { useState, useEffect } from 'react';
import { Activity, ExternalLink, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceMeters } from '../utils/distanceUtils';
import { useFetch } from '../hooks/useApi';
import { apiService } from '../services/apiService';
import { toast } from 'sonner';

const StravaConnection = () => {
  const { user } = useAuth();
  const displayUnit = user?.distance_unit || 'miles';
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);

  // Fetch Strava connection status
  const { data: stravaStatus, loading: statusLoading, refetch: refetchStatus } = useFetch(
    async () => {
      try {
        return await apiService.strava.getStatus();
      } catch (error) {
        // Silently handle 401 - user just needs to connect Strava
        if (error.response?.status === 401) {
          return null;
        }
        throw error;
      }
    },
    [user?.id],
    { enabled: !!user?.id }
  );

  // Fetch Strava activities
  const { data: stravaActivities, loading: activitiesLoading, refetch: refetchActivities } = useFetch(
    () => apiService.strava.getActivities(),
    [user?.id, connectionStatus?.is_active],
    { enabled: !!user?.id && connectionStatus?.is_active }
  );

  // Fetch Strava stats
  const { data: stravaStats, loading: statsLoading, refetch: refetchStats } = useFetch(
    () => apiService.strava.getStats(),
    [user?.id, connectionStatus?.is_active],
    { enabled: !!user?.id && connectionStatus?.is_active }
  );


  // Update local state when data changes
  useEffect(() => {
    if (stravaStatus) {
      setConnectionStatus(stravaStatus);
    } else {
      setConnectionStatus(null);
    }
  }, [stravaStatus]);

  // Handle errors gracefully - don't show errors for 401s (user just needs to connect)
  useEffect(() => {
    // Errors are handled by useFetch, we just need to make sure we don't crash
    // 401 errors are expected if user hasn't connected Strava yet
  }, []);

  useEffect(() => {
    if (stravaActivities) {
      setActivities(stravaActivities);
    }
  }, [stravaActivities]);

  useEffect(() => {
    if (stravaStats) {
      setStats(stravaStats);
    }
  }, [stravaStats]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // Get authorization URL
      const response = await apiService.strava.getAuthorizationUrl();
      if (response?.authorization_url) {
        // Redirect to Strava OAuth
        window.location.href = response.authorization_url;
      } else {
        toast.error('Failed to get Strava authorization URL');
      }
    } catch (error) {
      console.error('Failed to connect to Strava:', error);
      toast.error('Failed to connect to Strava');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await apiService.strava.disconnect();
      setConnectionStatus(null);
      setActivities([]);
      setStats(null);
      toast.success('Strava account disconnected successfully');
    } catch (error) {
      console.error('Failed to disconnect Strava:', error);
      toast.error('Failed to disconnect Strava account');
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response = await apiService.strava.sync();
      if (response?.activities) {
        setActivities(prev => [...response.activities, ...prev]);
        toast.success(`Synced ${response.activities.length} new activities`);
      }
      // Refresh stats after sync
      refetchStats();
    } catch (error) {
      console.error('Failed to sync activities:', error);
      toast.error('Failed to sync Strava activities');
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDistance = (meters) => formatDistanceMeters(meters, displayUnit);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (statusLoading) {
    return (
      <Card className="bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span className="ml-2 text-white/80">Loading Strava connection...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl">
      <CardHeader>
        <CardTitle className="text-white flex items-center" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
          <Activity className="w-6 h-6 mr-3 text-orange-400" />
          Strava Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!connectionStatus ? (
          // Not connected
          <div className="text-center space-y-4">
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <Activity className="w-12 h-12 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                Connect Your Strava Account
              </h3>
              <p className="text-white/80 text-sm mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Sync your running activities and track your progress with Runcards
              </p>
              <Button
                onClick={handleConnect}
                disabled={isConnecting}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                {isConnecting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect to Strava
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Connected
          <div className="space-y-6">
            {/* Connection Status */}
            <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-green-400 mr-3" />
                <div>
                  <h3 className="text-white font-semibold" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                    Connected to Strava
                  </h3>
                  <p className="text-white/80 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {connectionStatus.athlete_name && `Connected as ${connectionStatus.athlete_name}`}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDisconnect}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                Disconnect
              </Button>
            </div>

            {/* Stats */}
            {stats && (
              <div className="space-y-6">
                {/* Total Stats */}
                <div>
                  <h4 className="text-white font-semibold mb-3" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                    All Time Stats
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="text-2xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                        {stats.total_activities}
                      </div>
                      <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Activities
                      </div>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="text-2xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                        {formatDistance(stats.total_distance)}
                      </div>
                      <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Total Distance
                      </div>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="text-2xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                        {formatTime(stats.total_time)}
                      </div>
                      <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Total Time
                      </div>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <div className="text-2xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                        {Math.round(stats.total_elevation_gain)}m
                      </div>
                      <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Elevation
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weekly Stats */}
                {stats.weekly_stats && (
                  <div>
                    <h4 className="text-white font-semibold mb-3" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                      This Week
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {stats.weekly_stats.activities || 0}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Activities
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {formatDistance(stats.weekly_stats.distance || 0)}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Distance
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {formatTime(stats.weekly_stats.moving_time || 0)}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Time
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {Math.round(stats.weekly_stats.elevation_gain || 0)}m
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Elevation
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Monthly Stats */}
                {stats.monthly_stats && (
                  <div>
                    <h4 className="text-white font-semibold mb-3" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                      This Month
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {stats.monthly_stats.activities || 0}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Activities
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {formatDistance(stats.monthly_stats.distance || 0)}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Distance
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {formatTime(stats.monthly_stats.moving_time || 0)}
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Time
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <div className="text-xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                          {Math.round(stats.monthly_stats.elevation_gain || 0)}m
                        </div>
                        <div className="text-white/60 text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Elevation
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sync Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleSync}
                disabled={isSyncing}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                {isSyncing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Activities
                  </>
                )}
              </Button>
            </div>

            {/* Recent Activities */}
            {activities.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                  Recent Activities
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {activities.slice(0, 5).map((activity, index) => (
                    <div key={activity.id || index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex-1">
                        <div className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          {activity.name}
                        </div>
                        <div className="text-white/60 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          {formatDate(activity.start_date)} • {formatDistance(activity.distance)} • {formatTime(activity.moving_time)}
                        </div>
                      </div>
                      <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                        {activity.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StravaConnection;
